/**
 * 推荐海报生成 Modal
 * 一键生成带二维码 + 个人推荐码的海报图(可下载到本地 / 分享到朋友圈)
 *
 * 海报内容(800 x 1200):
 *   - 顶部:蜜蜂空间 logo + 副标题 + 当前用户
 *   - 中部:大二维码(QR 码内容 = 分享 URL)
 *   - 底部:个人推荐码 + 福利说明 + 邀请语
 */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Download, Share2, Copy, Loader2, Image as ImageIcon,
  Sparkles, QrCode
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useUser } from '../stores/user.js';
import { api } from '../lib/api.js';

// 简单 QR 码生成 — 用免费的 qrserver.com API
// (避免引入 100KB+ 的 jsQR 库)
function generateQRUrl(text, size = 320) {
  // api.qrserver.com 是免费的 QR API,支持高分辨率
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&bgcolor=ffffff&color=000000&margin=2`;
}

export function PosterModal({ open, onClose }) {
  const { user } = useUser();
  const [me, setMe] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [posterUrl, setPosterUrl] = useState(null);  // 完整海报图(可下载)
  const [referralCode, setReferralCode] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const r = await api.referrals.me();
        if (r?.user) {
          setMe(r.user);
          setReferralCode(r.user.referral_code);
          // 分享 URL
          const shareUrl = r.share_url || `https://${window.location.hostname}/?ref=${r.user.referral_code}`;
          setQrUrl(generateQRUrl(shareUrl, 320));
        }
      } catch (e) {
        console.warn('加载推荐码失败:', e.message);
        // 兜底:用 user 自己的推荐码
        if (user?.referralCode) {
          setReferralCode(user.referralCode);
          setQrUrl(generateQRUrl(`https://${window.location.hostname}/?ref=${user.referralCode}`, 320));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [open, user]);

  // 生成完整海报(用 Canvas 合成)
  const generatePoster = async () => {
    if (!qrUrl) return toast.error('二维码未生成');
    setGenerating(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 1200;
      const ctx = canvas.getContext('2d');

      // 1. 背景渐变
      const bg = ctx.createLinearGradient(0, 0, 0, 1200);
      bg.addColorStop(0, '#0f172a');
      bg.addColorStop(0.5, '#1e1b4b');
      bg.addColorStop(1, '#0f172a');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, 800, 1200);

      // 2. 装饰: 顶部光晕
      const radial1 = ctx.createRadialGradient(400, 100, 0, 400, 100, 600);
      radial1.addColorStop(0, 'rgba(168, 85, 247, 0.4)');
      radial1.addColorStop(1, 'rgba(168, 85, 247, 0)');
      ctx.fillStyle = radial1;
      ctx.fillRect(0, 0, 800, 600);

      const radial2 = ctx.createRadialGradient(400, 1000, 0, 400, 1000, 500);
      radial2.addColorStop(0, 'rgba(34, 211, 238, 0.3)');
      radial2.addColorStop(1, 'rgba(34, 211, 238, 0)');
      ctx.fillStyle = radial2;
      ctx.fillRect(0, 700, 800, 500);

      // 3. 顶部 Logo
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 72px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('蜜蜂空间', 400, 120);

      ctx.font = '24px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#a78bfa';
      ctx.fillText('智能杀号专业版', 400, 160);

      // 4. 副标题
      ctx.font = 'bold 36px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText('邀你一起 选号赚钱', 400, 230);

      ctx.font = '22px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#cbd5e1';
      ctx.fillText('注册即送 24h Pro 试用', 400, 270);

      // 5. 二维码白底框
      const qrSize = 360;
      const qrX = (800 - qrSize) / 2;
      const qrY = 340;
      // 阴影
      ctx.shadowColor = 'rgba(168, 85, 247, 0.5)';
      ctx.shadowBlur = 40;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40);
      ctx.shadowBlur = 0;

      // 6. 加载二维码图片
      const qrImg = await loadImage(qrUrl);
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

      // 7. 推荐码大字
      ctx.font = 'bold 60px JetBrains Mono, monospace';
      ctx.fillStyle = '#fbbf24';
      ctx.fillText(referralCode || 'LOADING', 400, 800);

      // 8. "扫一扫 / 长按识别"
      ctx.font = '20px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#94a3b8';
      ctx.fillText('长按识别二维码 · 注册即得推荐码', 400, 850);

      // 9. 福利分割线
      ctx.strokeStyle = 'rgba(168, 85, 247, 0.5)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(100, 920);
      ctx.lineTo(700, 920);
      ctx.stroke();

      // 10. 福利说明
      ctx.font = 'bold 28px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText('🎁 邀请奖励', 400, 970);

      ctx.font = '22px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#cbd5e1';
      ctx.fillText('✓ 好友充值,你拿 15% 佣金(立即到账冻结7天)', 400, 1010);
      ctx.fillText('✓ 好友的队友充值,你再拿 10%', 400, 1045);
      ctx.fillText('✓ 好友的队友的队友充值,你再拿 5%', 400, 1080);
      ctx.fillText('✓ 满 ¥100 即可提现到微信/支付宝', 400, 1115);

      // 11. 底部水印
      ctx.font = '18px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#64748b';
      ctx.fillText('扫码注册 · 一起玩转 蜜蜂空间', 400, 1170);

      // 12. 转 dataURL
      const dataUrl = canvas.toDataURL('image/png', 0.92);
      setPosterUrl(dataUrl);
      toast.success('海报已生成');
    } catch (e) {
      toast.error('生成失败: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  const downloadPoster = () => {
    if (!posterUrl) return;
    const a = document.createElement('a');
    a.href = posterUrl;
    a.download = `3dmax-推荐码-${referralCode || 'me'}.png`;
    a.click();
    toast.success('已下载,去朋友圈分享吧');
  };

  const copyCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success(`已复制推荐码:${referralCode}`);
  };

  const copyShareText = () => {
    const url = `https://${window.location.hostname}/?ref=${referralCode}`;
    navigator.clipboard.writeText(`我用 蜜蜂空间·选号分析工具,挺准的!注册链接: ${url} 我的推荐码:${referralCode}`);
    toast.success('已复制分享文案');
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={e => e.stopPropagation()}
          className="w-full sm:max-w-md glass-strong rounded-2xl p-5 relative max-h-[90vh] overflow-y-auto"
        >
          <button onClick={onClose} className="absolute top-3 right-3 text-ink-3 hover:text-ink-0">
            <X size={20} />
          </button>

          <div className="text-center mb-3">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-purple to-brand-cyan mb-2">
              <ImageIcon size={22} className="text-white" />
            </div>
            <h2 className="text-lg font-bold">生成推荐海报</h2>
            <p className="text-xs text-ink-3 mt-1">分享到朋友圈,好友扫码注册</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 size={24} className="mx-auto animate-spin text-brand-cyan" />
              <div className="text-xs text-ink-3 mt-2">加载中...</div>
            </div>
          ) : (
            <>
              {/* 推荐码 + 链接 */}
              <div className="p-3 rounded-xl bg-bg-base/50 border border-white/5 mb-3">
                <div className="text-[10px] text-ink-3 mb-1">我的推荐码</div>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold font-mono text-brand-gold tracking-wider">
                    {referralCode || '—'}
                  </div>
                  <button onClick={copyCode} className="btn-ghost text-xs flex items-center gap-1">
                    <Copy size={11} /> 复制
                  </button>
                </div>
              </div>

              {/* 预览 / 已生成海报 */}
              <div className="rounded-xl overflow-hidden border border-white/10 bg-bg-base mb-3" style={{ aspectRatio: '2/3' }}>
                {posterUrl ? (
                  <img src={posterUrl} alt="推荐海报" className="w-full h-full object-contain" />
                ) : qrUrl ? (
                  <div className="w-full h-full flex flex-col items-center justify-center p-4 bg-gradient-to-br from-bg-base via-brand-purple/10 to-brand-cyan/5">
                    <div className="bg-white p-2 rounded-lg mb-3">
                      <img src={qrUrl} alt="QR" className="w-40 h-40" />
                    </div>
                    <div className="text-2xl font-bold font-mono text-brand-gold">{referralCode}</div>
                    <div className="text-[10px] text-ink-3 mt-1">长按识别二维码</div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ink-3 text-xs">暂无预览</div>
                )}
              </div>

              {/* 操作按钮 */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button
                  onClick={generatePoster}
                  disabled={generating}
                  className="py-2.5 rounded-xl bg-gradient-to-r from-brand-purple to-brand-cyan text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {generating ? '生成中...' : (posterUrl ? '重新生成' : '生成完整海报')}
                </button>
                <button
                  onClick={downloadPoster}
                  disabled={!posterUrl}
                  className="py-2.5 rounded-xl bg-accent-success/20 border border-accent-success/40 text-accent-success text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-30"
                >
                  <Download size={12} /> 下载图片
                </button>
              </div>
              <button
                onClick={copyShareText}
                className="w-full py-2 rounded-lg bg-white/5 border border-white/10 text-xs flex items-center justify-center gap-1.5 hover:border-brand-cyan/40"
              >
                <Share2 size={12} /> 复制分享文案(推荐码 + 链接)
              </button>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// 加载图片 helper
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
